/**
 * Two-bone IK + terrain foot planting.
 *
 * Only the hero runs this — 500 crowd units get their feet from the baked
 * clips — but it is the single cheapest thing that stops the main character
 * from ice-skating over a slope, which is exactly where a Dynasty Warriors
 * screenshot gives itself away.
 *
 * The solver corrects ANGLES rather than rebuilding orientations from scratch,
 * so whatever twist the animation put into the leg survives the fix-up.
 */
import * as THREE from 'three';
import { BONE_INDEX } from '../chars/rig.js';

const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Vector3();
const _t = new THREE.Vector3(), _ab = new THREE.Vector3(), _cb = new THREE.Vector3();
const _at = new THREE.Vector3(), _ac = new THREE.Vector3(), _axis = new THREE.Vector3();
const _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _qp = new THREE.Quaternion();
const _n = new THREE.Vector3(), _up = new THREE.Vector3(0, 1, 0), _xa = new THREE.Vector3(1, 0, 0);
const _s1 = new THREE.Vector3(), _s2 = new THREE.Vector3();
const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

/** Rotate a bone by a world-space rotation `r`, keeping its parent chain. */
function applyWorld(bone, r) {
  bone.getWorldQuaternion(_q2);
  _q2.premultiply(r);
  if (bone.parent) { bone.parent.getWorldQuaternion(_qp); _qp.invert(); _q2.premultiply(_qp); }
  bone.quaternion.copy(_q2);
}

/**
 * Classic three-point solver. `root`/`mid`/`tip` are THREE.Bones; `target` a
 * world-space Vector3; `pole` an optional world-space hint for the bend plane.
 */
export function twoBoneIK(root, mid, tip, target, pole, weight = 1) {
  root.getWorldPosition(_a); mid.getWorldPosition(_b); tip.getWorldPosition(_c);
  _t.copy(target);
  if (weight < 1) _t.lerpVectors(_c, _t, weight);
  const lab = _a.distanceTo(_b), lcb = _b.distanceTo(_c);
  if (lab < 1e-5 || lcb < 1e-5) return;
  const maxLen = (lab + lcb) * 0.995;
  let lat = _a.distanceTo(_t);
  if (lat > maxLen) { _t.sub(_a).multiplyScalar(maxLen / lat).add(_a); lat = maxLen; }
  const minLen = Math.abs(lab - lcb) * 1.02 + 1e-4;
  if (lat < minLen) { if (lat < 1e-5) return; _t.sub(_a).multiplyScalar(minLen / lat).add(_a); lat = minLen; }

  _ab.subVectors(_b, _a).normalize();
  _ac.subVectors(_c, _a).normalize();
  _cb.subVectors(_b, _c).normalize();
  _at.subVectors(_t, _a).normalize();

  const acAb = Math.acos(clamp(_ac.dot(_ab), -1, 1));
  _s1.copy(_ab).negate(); _s2.copy(_cb).negate();
  const baBc = Math.acos(clamp(_s1.dot(_s2), -1, 1));
  const acAbT = Math.acos(clamp((lcb * lcb - lab * lab - lat * lat) / (-2 * lab * lat), -1, 1));
  const baBcT = Math.acos(clamp((lat * lat - lab * lab - lcb * lcb) / (-2 * lab * lcb), -1, 1));

  _s1.copy(_ab).multiplyScalar(-1);
  _axis.subVectors(_c, _a).cross(_s1);
  if (_axis.lengthSq() < 1e-8 && pole) _axis.subVectors(pole, _a).cross(_at);
  if (_axis.lengthSq() < 1e-8) _axis.set(1, 0, 0);
  _axis.normalize();

  _q.setFromAxisAngle(_axis, acAbT - acAb);
  applyWorld(root, _q);
  root.updateMatrixWorld(true);

  _q.setFromAxisAngle(_axis, baBcT - baBc);
  applyWorld(mid, _q);
  mid.updateMatrixWorld(true);

  // finally swing the whole limb so the tip lands on the target
  tip.getWorldPosition(_c);
  _ac.subVectors(_c, _a).normalize();
  _at.subVectors(_t, _a).normalize();
  _q.setFromUnitVectors(_ac, _at);
  applyWorld(root, _q);
  root.updateMatrixWorld(true);
}

/**
 * Per-foot ground lock. Module scope: only the hero ever runs the solver, and
 * a hot path must not allocate. `w` is how much of the lock is engaged (it
 * fades in as the foot lands and out as it lifts), `x/z` the world point the
 * foot was nailed to when it landed.
 */
const LOCK = { owner: null, w: new Float32Array(2), x: new Float32Array(2), z: new Float32Array(2) };

/** Forget the current plant (call on teleport / respawn). */
export function resetFootLock() { LOCK.w[0] = 0; LOCK.w[1] = 0; }

/**
 * What the lock ASKED FOR against what the solver delivered, per foot, in
 * metres, refreshed every plantFeet() call.
 *
 * `want` is how far the ankle had to move to reach its plant point and `miss`
 * is how much of that the two-bone solver could not deliver, which is a
 * different quantity from either: the horizontal lock can be computing a
 * perfect target and still leave the foot creeping if that target is past the
 * leg's reach. At the stance-to-swing boundary — where r3 measured every one
 * of its worst frames — the hips have travelled furthest from the plant and
 * the leg is nearest full extension, so that is exactly where the solver runs
 * out of leg. Reported so the next attempt at this measures which of the two
 * it is before changing anything; a release-curve tune has already been tried
 * against the wrong one and came back as noise.
 */
export const IK_STATS = { want: new Float32Array(2), miss: new Float32Array(2), down: new Float32Array(2) };

/**
 * Plant both feet on the terrain.
 *
 * Two corrections, in this order:
 *   1. VERTICAL   — lift the pelvis / drop the ankle so the sole meets the
 *      ground it is standing on rather than the ground the clip assumed.
 *   2. HORIZONTAL — the foot lock. The clips are authored so a planted ankle
 *      already tracks the ground (see gait.js), but a blend between two gaits,
 *      a slope, or a speed change mid-stance still leaves centimetres of creep.
 *      The instant a foot lands its world x/z is captured, and for as long as
 *      it stays down the IK pulls the ankle back to that exact point — so the
 *      foot is nailed to the earth, not merely close to it. The lock releases
 *      over `slip` metres so a genuine step or a hard turn is never fought.
 *
 * @param {THREE.SkinnedMesh} mesh    the hero mesh (world transform applied)
 * @param {THREE.Bone[]} bones
 * @param {{heightAt(x,z):number, normalAt(x,z,out):THREE.Vector3}} terrain
 * @param {{weight?:number, lift?:number, maxDrop?:number, ankle?:number,
 *          lock?:number, slip?:number, band?:number, hold?:number, vband?:number}} o
 * @returns {number} the pelvis lift that was applied (metres)
 */
export function plantFeet(mesh, bones, terrain, o = {}) {
  if (!terrain) return 0;
  const w = o.weight ?? 1;
  if (w <= 0) return 0;
  const ankle = o.ankle ?? 0.085;
  const maxLift = o.lift ?? 0.28;
  const root = bones[BONE_INDEX.root];
  mesh.updateMatrixWorld(true);

  const legs = [
    [bones[BONE_INDEX.thighL], bones[BONE_INDEX.shinL], bones[BONE_INDEX.footL]],
    [bones[BONE_INDEX.thighR], bones[BONE_INDEX.shinR], bones[BONE_INDEX.footR]],
  ];
  // 1. how far up does each ankle need to travel to clear the ground?
  let lift = 0;
  const wantY = [0, 0];
  for (let i = 0; i < 2; i++) {
    legs[i][2].getWorldPosition(_c);
    const g = terrain.heightAt(_c.x, _c.z) + ankle;
    wantY[i] = g;
    lift = Math.max(lift, g - _c.y);
  }
  lift = clamp(lift, 0, maxLift) * w;
  if (lift > 1e-4) { root.position.y += lift; root.updateMatrixWorld(true); }

  // 2. pull each ankle down (or up) onto its ground point, and hold it there
  const lockW = o.lock ?? 1;
  const slip = o.slip ?? 0.30;
  const band = o.band ?? 0.075;                 // ankle height that counts as "down"
  // Hysteresis on the HORIZONTAL lock. Stance is not flat: the ankle rides up
  // ~9 cm through toe-off as the heel lifts (gait.js authors yToe well above
  // yMid, which is what a push-off is), so a lock that releases at `band` lets
  // go a quarter of the way through every step and the foot creeps for the
  // rest of it. Once a foot IS down it stays horizontally nailed until it is
  // `hold` metres up — past the whole toe-off, still under a real step.
  const hold = o.hold ?? 0.185;
  // The VERTICAL pull keeps its own narrow band on purpose. A heel lifting at
  // toe-off is the animation, not an error: pulling the ankle back down over
  // the wide band flattens the push-off and the run goes shuffling. Only an
  // ankle genuinely near the ground — mid-stance, where a slope or a gait
  // cross-fade really can float it — is pulled onto its ground height.
  const vband = o.vband ?? 0.055;
  if (LOCK.owner !== mesh) { LOCK.owner = mesh; LOCK.w[0] = 0; LOCK.w[1] = 0; }
  for (let i = 0; i < 2; i++) {
    const [th, sh, ft] = legs[i];
    ft.getWorldPosition(_c);
    const gy = wantY[i];

    // --- horizontal lock ---------------------------------------------------
    let tx = _c.x, tz = _c.z;
    // 1 when the ankle is on the ground, fading to 0 as it lifts. The release
    // band widens once the foot is already planted (see `hold` above).
    const b = LOCK.w[i] > 0.02 ? hold : band;
    // Ease the release OUT, do not ramp it linearly. `down` scales the whole
    // correction, so a straight ramp leaves the last third of stance holding
    // at a third strength and the foot creeps exactly there: measured at r3,
    // mean in-stance slide was a good 0.36-0.60 m/s but the worst frames hit
    // 1.21 m/s and every one of them sat on the stance-to-swing boundary.
    // 1-(1-d)^2 keeps the lock near full through toe-off and drops it at the
    // very top of the band, where the foot really is leaving.
    const dRaw = clamp(1 - (_c.y - gy) / b, 0, 1);
    const down = dRaw * (2 - dRaw);
    if (lockW > 0) {
      if (down > 0.02) {
        if (LOCK.w[i] <= 0.02) { LOCK.x[i] = _c.x; LOCK.z[i] = _c.z; }
        let dx = LOCK.x[i] - _c.x, dz = LOCK.z[i] - _c.z;
        const d = Math.sqrt(dx * dx + dz * dz);
        if (d > slip) {                          // walked out of the lock: slide it
          const k = (d - slip) / d;
          LOCK.x[i] -= dx * k; LOCK.z[i] -= dz * k;
          dx = LOCK.x[i] - _c.x; dz = LOCK.z[i] - _c.z;
        }
        const k = down * lockW * w;
        tx = _c.x + dx * k; tz = _c.z + dz * k;
      }
      LOCK.w[i] = down;
    }
    // --- vertical lock -----------------------------------------------------
    let ty = _c.y < gy ? gy : _c.y;
    if (lockW > 0) {
      const vd = clamp(1 - (_c.y - gy) / vband, 0, 1);
      if (vd > 0.02) ty += (gy - ty) * vd * lockW * w;
    }
    IK_STATS.down[i] = down;
    if (ty === _c.y && tx === _c.x && tz === _c.z) {                 // in the air
      IK_STATS.want[i] = 0; IK_STATS.miss[i] = 0;
      continue;
    }
    _t.set(tx, ty, tz);
    IK_STATS.want[i] = Math.hypot(tx - _c.x, tz - _c.z);
    twoBoneIK(th, sh, ft, _t, null, w);
    ft.getWorldPosition(_c);
    IK_STATS.miss[i] = Math.hypot(tx - _c.x, tz - _c.z);
    // roll the foot onto the slope
    if (terrain.normalAt) {
      terrain.normalAt(_c.x, _c.z, _n);
      _q.setFromUnitVectors(_up, _n);
      _q2.identity().slerp(_q, 0.55 * w);
      applyWorld(ft, _q2);
    }
  }
  root.updateMatrixWorld(true);
  return lift;
}

/** Aim the head (and a little of the chest) at a world point. */
export function lookAt(bones, target, weight = 1, o = {}) {
  if (weight <= 0) return;
  const head = bones[BONE_INDEX.head], neck = bones[BONE_INDEX.neck], chest = bones[BONE_INDEX.chest];
  const maxYaw = o.maxYaw ?? 1.1, maxPitch = o.maxPitch ?? 0.55;
  head.getWorldPosition(_a);
  _at.subVectors(target, _a);
  const d = _at.length();
  if (d < 0.05) return;
  _at.multiplyScalar(1 / d);
  // express the direction in the neck's parent space so limits stay sane
  chest.getWorldQuaternion(_qp); _qp.invert();
  _ac.copy(_at).applyQuaternion(_qp);
  let yaw = Math.atan2(_ac.x, _ac.z);
  let pitch = -Math.asin(clamp(_ac.y, -1, 1));
  yaw = clamp(yaw, -maxYaw, maxYaw); pitch = clamp(pitch, -maxPitch, maxPitch);
  _q.setFromAxisAngle(_up, yaw * 0.34 * weight);
  _q2.setFromAxisAngle(_xa, pitch * 0.30 * weight);
  neck.quaternion.multiply(_q).multiply(_q2);
  _q.setFromAxisAngle(_up, yaw * 0.62 * weight);
  _q2.setFromAxisAngle(_xa, pitch * 0.62 * weight);
  head.quaternion.multiply(_q).multiply(_q2);
}
